import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  AlertIcon,
  BackButton,
  ChildrenIcon,
  DataSurface,
  InfoIcon,
  Note,
  PageHead,
  PlayIcon,
  SchoolIcon,
  SectionHead,
  StatCard,
  StatGrid,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Tr,
  GamesPanel,
  PageDateFilterBar,
  Pagination,
  usePagination,
} from '../../../shared/components'
import { exportToCsv, exportToExcel } from '../../../shared/lib/exportData'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getEngagement,
  getPlatformChild,
  getPlatformChildren,
  getPlatformGrowth,
  getPlatformOverview,
  getSchools,
  type EngagementOverview,
  type GrowthSeries,
  type PlatformChildDetail,
  type PlatformChildrenPage,
  type PlatformOverview,
  type SchoolSummary,
} from '../../../shared/api/platform'
import { foldCensus, tilePair, tileValue } from './census'
import { GrowthChart } from './GrowthChart'
import { RoleSplit } from './RoleSplit'
import { GAMES_PANEL_COPY } from '../../../shared/ui/gamesCopy'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from '../../../shared/i18n'
import { PLATFORM_COPY, describePlatformError } from './copy'
import {
  MIN_ANSWERS_FOR_ACCURACY,
  accuracyPair,
  activityState,
  childrenToCsv as _childrenToCsv,
  filterChildren,
  minutesPlayed,
  type ActivityFilter,
  type ActivityState,
} from './childrenModel'

/**
 * 🔴 A WORD AND A COLOUR FOR EACH STATE, NEVER A COLOUR ALONE (§12). The four
 * are `childrenModel`'s, and "unrecorded" is the one that must not read as
 * "quiet": a child whose sittings were never written contributes zero to every
 * figure beside them, which is a recording setting rather than a child who did
 * not play (ADR-030).
 */
const ACTIVITY_LABEL: Record<ActivityState, string> = {
  RECENT: 'This week',
  EARLIER: 'Earlier',
  NEVER: 'Never played',
  UNRECORDED: 'Not recorded',
}

const ACTIVITY_TONE: Record<ActivityState, 'success' | 'neutral' | 'warning'> = {
  RECENT: 'success',
  EARLIER: 'neutral',
  NEVER: 'warning',
  UNRECORDED: 'warning',
}

/**
 * ⚠️ `StatusPill` REQUIRES AN ICON, AND THE TYPE IS WHAT ENFORCES §12 — colour
 * is never the status on its own. Two of these share a glyph deliberately:
 * "never played" and "not recorded" are both findings, and inventing a third
 * symbol to separate them would rely on the reader learning it.
 */
const ACTIVITY_ICON: Record<ActivityState, ReactNode> = {
  RECENT: <PlayIcon size={14} />,
  EARLIER: <InfoIcon size={14} />,
  NEVER: <AlertIcon size={14} />,
  UNRECORDED: <AlertIcon size={14} />,
}

/** The bands, spelt as a school says them rather than as the enum does. */
const BAND_LABEL: Record<string, string> = {
  BAND_3_4: '3–4',
  BAND_4_5: '4–5',
  BAND_5_6: '5–6',
}

/**
 * Every child on the platform — the screen F1's enrolment tile pointed at and
 * did not have.
 *
 * 🔴 THE TILE SAID "Children enrolled: 412" AND THERE WAS NOWHERE TO GO. Every
 * question next to that number — *is that roster real*, *which of them have
 * never played*, *the parent who rang about their daughter, has she played at
 * all* — ended at a total. This is the drill-down, and it is reached by clicking
 * the tile rather than from a sidebar row, because it answers a question the
 * dashboard raises.
 *
 * 🔴 EVERY FIGURE IS READ FROM `Event` AND `Session`. A child who has never
 * played reports zeroes and "Never", which is a fact about them rather than a
 * gap in a query — and there is no fixture in this file. §8.4's warning is
 * loudest on the tier that can see every school at once.
 *
 * 🔴 THE FILTERS RUN IN THE BROWSER OVER ONE RESPONSE, AND THAT IS AN AUDIT
 * DECISION AS MUCH AS A LATENCY ONE. A server-side `?q=` would write an audited
 * read per keystroke, whose summary would either carry a child's name into a
 * table retained beyond the ordinary window or be indistinguishable from forty
 * others. One read, one row, one sentence.
 */

export interface PlatformChildrenScreenProps {
  lang?: Lang
  auth?: StaffAuth
}

export function PlatformChildrenScreen({ lang = 'en', auth }: PlatformChildrenScreenProps) {
  const t = PLATFORM_COPY[lang]

  const [query, setQuery] = useState('')
  const [schoolId, setSchoolId] = useState<string | null>(null)
  const [activity, setActivity] = useState<ActivityFilter>('ALL')
  const [unattributedOnly, setUnattributedOnly] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  /*
   * ⚠️ THE PAGE SIZE IS STATE, AND THE PAGE NUMBER IS THE HOOK'S. `usePagination`
   * already clamps the current page against the total, so narrowing a filter
   * from page nine cannot strand somebody on an empty page.
   */
  const [pageSize, setPageSize] = useState(25)

  const resource = useResource<PlatformChildrenPage>(
    (signal) => getPlatformChildren({ ...auth, signal }),
    ['platform-children'],
    { isEmpty: (page) => page.rows.length === 0 },
  )

  /*
   * 🔴 THE FOUR READS THE STAT ROW AND THE THREE CHARTS ABOVE THE TABLE NEEDED
   * AND NEVER MADE. Every tile on this screen was a hardcoded em dash under a
   * hardcoded 'Not measured yet', and 'Active Users by Role' printed Teachers
   * 892 · Parents 328 · School Admins 87 beside them — on a platform with five
   * teachers and four parents. All four endpoints have shipped since the tier
   * was built; nothing was missing except the call.
   *
   * ⚠️ THEY ARE SEPARATE RESOURCES, NOT ONE COMBINED FETCH. `foldCensus` takes
   * nulls precisely so a slow or failing engagement read leaves the student and
   * teacher tiles standing rather than blanking the row — a screen that hides
   * four true figures because a fifth is late is a screen an operator learns to
   * distrust.
   */
  const overviewResource = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }),
    ['platform-overview'],
  )
  const engagementResource = useResource<EngagementOverview>(
    (signal) => getEngagement({ ...auth, signal }),
    ['platform-engagement'],
  )
  const schoolsResource = useResource<SchoolSummary[]>(
    (signal) => getSchools({ ...auth, signal }),
    ['platform-schools'],
  )
  const growthResource = useResource<GrowthSeries>(
    (signal) => getPlatformGrowth(30, { ...auth, signal }),
    ['platform-growth', 30],
  )

  const census = useMemo(
    () =>
      foldCensus(
        overviewResource.data,
        engagementResource.data,
        schoolsResource.data,
        growthResource.data,
      ),
    [overviewResource.data, engagementResource.data, schoolsResource.data, growthResource.data],
  )

  /* The schools an operator may filter by — the ones that exist, not four invented names. */
  const schoolOptions = useMemo(
    () => [...(schoolsResource.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [schoolsResource.data],
  )

  const rows = useMemo(() => resource.data?.rows ?? [], [resource.data])

  /*
   * 🔴 ONE CLOCK FOR THE WHOLE SCREEN, ANCHORED TO THE LOAD. `activityState` is
   * asked about every child and again on every keystroke; a fresh `new Date()`
   * per call compares the last rows against a later instant than the first —
   * invisible at seven days and wrong exactly at the boundary the "quiet for a
   * week" filter lives on. It also stops the pills silently changing meaning
   * while an operator reads the page. `SchoolStudentsScreen` does the same.
   */
  const now = useMemo(() => resource.lastLoadedAt ?? new Date(), [resource.lastLoadedAt])

  /*
   * ✅ ONE REQUEST, PAGINATED IN THE BROWSER, AND THAT IS THE FAST SHAPE HERE
   * RATHER THAN THE LAZY ONE. `GET /superadmin/children` is a single audited
   * read capped at 2000 rows (`CHILD_PAGE_MAX`); paging it server-side would
   * mean a round trip — and an audit row — per page turn and per keystroke, and
   * `ChildrenService` argues at length why the search deliberately does not go
   * to the server. Slicing an array that is already in memory is instant, and
   * only `pageSize` rows are ever rendered, so a 2000-row platform draws 25.
   */
  const shown = useMemo(
    () => filterChildren(rows, { query, schoolId, activity, unattributedOnly }, now),
    [rows, query, schoolId, activity, unattributedOnly, now],
  )

  /* The page the operator is on, clamped against the rows that survive the
     filters — see `usePagination`. */
  const { page, totalPages, setPage, paginatedItems: paged } = usePagination(shown, pageSize)

  const clear = useCallback(() => {
    setQuery('')
    setSchoolId(null)
    setActivity('ALL')
    setUnattributedOnly(false)
  }, [])

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  /*
   * 🔴 THE CHILD REPLACES THE LIST RATHER THAN SITTING BESIDE IT. A drawer over
   * four hundred rows would let an operator scroll a roster of other people's
   * children while one child's record is open — and the audit row they have just
   * written names that one child, so the screen should be about them.
   */
  if (openId !== null) {
    return (
      <ChildDetailScreen
        playerId={openId}
        lang={lang}
        auth={auth}
        onBack={() => setOpenId(null)}
      />
    )
  }

  const handleChildrenExportExcel = () => {
    const exportData = shown.map((c, idx) => ({
      '#': idx + 1,
      'Student ID': c.playerId,
      'Student Name': c.name,
      'Institution': c.schoolName ?? 'Unassigned',
      'Class / Grade': c.grade ?? '—',
      'Parent Name': c.parentName ?? '—',
      'Games Played': c.gamesPlayed,
      'Total Answers': c.correct + c.wrong,
      'First Played': c.firstPlayedAt ?? '—',
      'Last Played': c.lastPlayedAt ?? '—',
    }))
    exportToExcel('Platform_Students_Master_Report', exportData, {
      totalStudents: shown.length,
      /*
       * 🔴 THESE READ 2143, 2089 AND 124 — THREE INVENTED TOTALS WRITTEN INTO A
       * FILE THAT LEAVES THE BUILDING. A number on a screen can be checked
       * against the screen; a number in a downloaded report headed
       * "Platform Students Master Report" gets forwarded, quoted and filed. The
       * real figures were three lines up the same component the whole time —
       * `census` folds them from the platform overview.
       */
      totalTeachers: census.teachers ?? 0,
      totalParents: census.parents ?? 0,
      totalInstitutions: schoolsResource.data?.length ?? 0,
    })
  }

  const handleChildrenExportCsv = () => {
    const exportData = shown.map((c, idx) => ({
      '#': idx + 1,
      'Student ID': c.playerId,
      'Student Name': c.name,
      'Institution': c.schoolName ?? 'Unassigned',
      'Class / Grade': c.grade ?? '—',
      'Parent Name': c.parentName ?? '—',
      'Games Played': c.gamesPlayed,
      'Total Answers': c.correct + c.wrong,
      'First Played': c.firstPlayedAt ?? '—',
      'Last Played': c.lastPlayedAt ?? '—',
    }))
    exportToCsv('Platform_Students_Master_Report', exportData, {
      totalStudents: shown.length,
      /* The real three, for the reason the Excel export above states. */
      totalTeachers: census.teachers ?? 0,
      totalParents: census.parents ?? 0,
      totalInstitutions: schoolsResource.data?.length ?? 0,
    })
  }

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* 1. Top Header with Title & Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Students & Users</h1>
          <p className="mt-1 font-dash text-dash-sm text-muted">
            Manage all onboarded students and user accounts across the Kalqy platform.
          </p>
        </div>

        {/*
          ✅ THE "ADD USER" BUTTON IS GONE, AND WHAT IT OPENED IS WHY. The dialog
          behind it ended in `alert('User "X" (Student) has been created for Y')`
          and called nothing — no request, no account, no row. An operator who
          used it walked away believing a teacher could sign in.
          
          🔴 THERE IS NO PLATFORM-WIDE "CREATE ANY USER" ROUTE, AND THAT IS
          DELIBERATE RATHER THAN MISSING. Each kind of account is created where
          its rules live: an operator mints a school admin on that school's page
          (`POST /superadmin/schools/:id/admins`), a school admin creates a
          teacher on E3, a teacher enrols a child on E7, and a parent account
          comes into being when a family's number is linked. A single form that
          claimed to make any of the five would have to reimplement four sets of
          rules, and the one it replaced implemented none.
        */}
      </div>

      <PageDateFilterBar
        title="Students Filter Period"
        fromDate={fromDate}
        toDate={toDate}
        onApply={(from, to) => {
          setFromDate(from)
          setToDate(to)
        }}
        onExportExcel={handleChildrenExportExcel}
        onExportCsv={handleChildrenExportCsv}
      />

      {/* 2. Top Stat Cards Grid (6 Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: Total Users */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <ChildrenIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Users</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.totalUsers)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {census.totalUsers === null ? "Counting…" : "Students, teachers, parents and school admins"}
            </span>
          </div>
        </div>

        {/* Card 2: School Admins */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <SchoolIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">School Admins</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.schoolAdmins)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {census.schoolAdmins === null ? "Counting…" : "Active admins across every school"}
            </span>
          </div>
        </div>

        {/* Card 3: Teachers */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <SchoolIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Teachers</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.teachers)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {census.activeTeachers === null || census.engagementWindowDays === null ? "Active teacher accounts" : `${census.activeTeachers} signed in within ${census.engagementWindowDays} days`}
            </span>
          </div>
        </div>

        {/* Card 4: Students */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Students</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.students)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Children holding a seat"}
            </span>
          </div>
        </div>

        {/* Card 5: Parents */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
              <ChildrenIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Parents</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.parents)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {census.activeParents === null || census.engagementWindowDays === null ? "Parents the platform has reached" : `${census.activeParents} active in ${census.engagementWindowDays} days`}
            </span>
          </div>
        </div>

        {/* Card 6: Active Users (7D) */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center shrink-0">
              <PlayIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Active Users (7D)</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(census.activeStudents)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {census.activeWindowDays === null ? "Children who played" : `Children who played in ${census.activeWindowDays} days`}
            </span>
          </div>
        </div>
      </div>

      {/* 3. Middle 4 Charts Row Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/*
          🔴 THREE OF THESE FOUR CARDS WERE DRAWN BY HAND AND FILLED WITH
          NOTHING — an empty ring over a hardcoded '—', an SVG holding a
          gradient definition and no path, and an axis reading Apr 20 – May 18
          on a screen opened in August. The fourth was worse: it printed
          Teachers 892 · Parents 328 · School Admins 87 with bar widths of 45%,
          25% and 12%, on a platform of five teachers and four parents.

          ⚠️ THEY ARE NOW THE COMPONENTS F1 ALREADY USES, NOT NEW ONES.
          `RoleSplit` and `GrowthChart` were built for the platform dashboard,
          take live responses, and carry their own designed empty states — the
          hand-rolled copies here were a second vocabulary for the same three
          questions, and the copy is what drifted into fiction.
        */}
        <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h3 className="font-dash text-dash-md font-extrabold text-ink mb-3">Users by role</h3>
          <div className="my-auto">
            <RoleSplit
              roles={[
                { key: 'students', label: 'Students', count: census.students ?? 0, tint: 'bg-emerald-500' },
                { key: 'teachers', label: 'Teachers', count: census.teachers ?? 0, tint: 'bg-blue-500' },
                { key: 'parents', label: 'Parents', count: census.parents ?? 0, tint: 'bg-purple-500' },
                { key: 'admins', label: 'School admins', count: census.schoolAdmins ?? 0, tint: 'bg-amber-500' },
              ]}
              total={census.totalUsers ?? 0}
              emptyMessage="Nobody on the platform yet."
            />
          </div>
        </div>

        {/*
          ⚠️ 'ACTIVE' HERE MEANS PLAYED OR SIGNED IN INSIDE THE SERVER'S OWN
          WINDOW, AND THE HEADING SAYS WHICH WINDOW. The card this replaces was
          headed 'Users by Status' with an Active / Inactive / Pending split —
          three states nothing on the platform records. What is measured is
          recency, and it is measured over a period the response carries.
        */}
        <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h3 className="font-dash text-dash-md font-extrabold text-ink">Who is actually using it</h3>
          <p className="text-dash-xs font-medium text-muted mb-3">
            {census.activeWindowDays === null
              ? 'Recently active, against the whole platform.'
              : `Active in the last ${census.activeWindowDays} days, against the whole platform.`}
          </p>
          <div className="my-auto space-y-3.5 text-dash-xs">
            {[
              { label: 'Children who played', active: census.activeStudents, of: census.students, tint: 'bg-emerald-500' },
              { label: 'Teachers signed in', active: census.activeTeachers, of: census.teachers, tint: 'bg-blue-500' },
              { label: 'Parents signed in', active: census.activeParents, of: census.parents, tint: 'bg-purple-500' },
            ].map((row) => (
              <div key={row.label}>
                <div className="flex justify-between font-bold text-ink mb-1">
                  <span>{row.label}</span>
                  {/* 🔴 A PAIR, NEVER A BARE PERCENTAGE — OD-39/OD-72. */}
                  <span className="tabular">{tilePair(row.active, row.of)}</span>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                  {/* ⚠️ THE BAR IS DRAWN FROM THE PAIR ABOVE IT, so it cannot
                      show a width the number beside it contradicts. Unknown is
                      a zero-width bar and a dash, not a guessed fill. */}
                  <div
                    className={`h-full ${row.tint} rounded-full`}
                    style={{
                      width:
                        row.active === null || row.of === null || row.of === 0
                          ? '0%'
                          : `${Math.min(100, Math.round((row.active / row.of) * 100))}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          {census.sampled ? (
            <p className="mt-3 text-dash-2xs font-semibold text-muted">
              The engagement read hit its cap — the teacher and parent figures are a sample.
            </p>
          ) : null}
        </div>

        <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h3 className="font-dash text-dash-md font-extrabold text-ink mb-3">User growth</h3>
          <DataSurface
            state={growthResource.state}
            skeletonRows={3}
            errorMessage={describePlatformError(growthResource.error, lang)}
            errorRetryLabel={t.retry}
            onRetry={growthResource.retry}
          >
            {growthResource.data === null ? null : <GrowthChart series={growthResource.data} />}
          </DataSurface>
        </div>
      </div>

      {/* 4. Filter Toolbar (Search + Role + Status + Institution + Location + Reset) */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[280px]">
          <input
            type="search"
            placeholder="Search users by name, email or ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-xl border border-line bg-white px-4 py-2 text-dash-xs pl-10 pr-4 focus:border-primary focus:outline-none transition-colors"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          {/*
            ✅ THE "ROLE" SELECT IS GONE. It offered Super Admin, School Admin,
            Teacher, Student and Parent over a table of `Player` rows, where the
            answer is Student for every row — five options, four of which matched
            nothing and one of which changed nothing. It was also wired to no
            state at all. D4: a control that cannot work teaches an operator the
            screen is unreliable.

            ✅ AND "STATUS" IS NOW ACTIVITY, WHICH IS WHAT IS ACTUALLY RECORDED.
            The old one offered Active / Inactive / Pending — three states
            nothing on this platform stores, and the same invention the chart
            above it already had removed for saying so. What exists is when a
            child last played, and `activityState` is the one definition of it.
          */}
          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted" htmlFor="children-activity">
              Activity
            </label>
            <select
              id="children-activity"
              value={activity}
              onChange={(e) => {
                setActivity(e.target.value as ActivityFilter)
              }}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All activity</option>
              <option value="RECENT">Played this week</option>
              <option value="QUIET">Quiet (7+ days)</option>
              <option value="NEVER">Never played</option>
              {/* 🔴 ADR-030 — sittings that happened and were never written. */}
              <option value="UNRECORDED">Not recorded</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Institution</label>
            {/*
              🔴 THE SCHOOLS THAT EXIST, NOT FOUR THAT DO NOT. This list read
              "ABC International School", "Sunrise Public School" and "Delhi
              Public School" — three names invented for a mockup, on the filter
              an operator uses to narrow a roster of real children. Choosing one
              matched nothing and looked like an empty school.
            */}
            <select
              value={schoolId ?? "ALL"}
              onChange={(e) => { setSchoolId(e.target.value === "ALL" ? null : e.target.value) }}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All Institutions</option>
              {schoolOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>


          {/*
            ✅ "MORE FILTERS" OPENED NOTHING — no panel, no handler, no state. In
            its place is the filter this screen already carried and never drew:
            `unattributedOnly` is F1's `unattributedChildren` by name, a child
            with no class and therefore no school, and it is the single most
            useful thing an operator can ask this table for.
          */}
          <label className="flex items-center gap-2 rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold text-ink shadow-xs cursor-pointer">
            <input
              type="checkbox"
              checked={unattributedOnly}
              onChange={(e) => {
                setUnattributedOnly(e.target.checked)
              }}
              className="h-3.5 w-3.5 cursor-pointer accent-primary"
            />
            <span>No school only</span>
          </label>

          <button
            type="button"
            onClick={clear}
            className="flex items-center gap-1.5 rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs cursor-pointer"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Reset</span>
          </button>
        </div>
      </div>

      {/*
        5. The children this platform actually has.

        ✅ THE ROWS WERE ALREADY LOADED AND WERE NEVER DRAWN. `getPlatformChildren`
        runs on mount, `filterChildren` narrows it on every keystroke, and the
        result reached exactly two places: the CSV button and the Excel button.
        The table under them rendered one hardcoded sentence — *"there is no
        platform-wide user directory yet"* — so an operator read "No users to
        show" on a screen whose own export downloaded thirty-three real children.

        🔴 AND THE PAGER SAID 1877. Five numbered buttons and a "1877" were
        static markup from the mockup this screen was built from: no state, no
        handler, no relationship to anything. At ten a page that claimed 18,770
        children on a platform with thirty-three.
      */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {/* 🔴 A COUNT WITH ITS DENOMINATOR (OD-39/OD-72), never a bare number,
                and it says when a filter is what is hiding the rest. */}
            <span className="text-dash-xs text-muted font-semibold">
              {rows.length === 0
                ? 'No children on the platform yet'
                : shown.length === rows.length
                  ? `${String(rows.length)} ${rows.length === 1 ? 'child' : 'children'}`
                  : `${String(shown.length)} of ${String(rows.length)} children match`}
            </span>
            {/* ⚠️ The cap, named. `PlatformChildrenPage.truncated` has been on the
                response since it was built and no screen ever drew it — a capped
                page that says nothing reads as a total. */}
            {resource.data?.truncated === true && (
              <span className="rounded-pill border border-pill-warning-line bg-pill-warning-bg px-2.5 py-0.5 text-dash-2xs font-bold text-pill-warning-fg">
                First {resource.data.limit} shown
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="text-dash-xs font-bold text-muted" htmlFor="children-page-size">
              Rows
            </label>
            <select
              id="children-page-size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
              }}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer font-bold"
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
        </div>

        <div>
          <TableCard>
            <table className="w-full border-collapse">
              <Thead
                headers={[
                  '#',
                  'Child',
                  'Roll',
                  'Class',
                  'School',
                  'Band',
                  'Sittings',
                  'Last active',
                  '',
                ]}
              />
              <tbody>
                {/*
                  ⚠️ THE COLUMNS ARE WHAT A CHILD HAS. The headers here read
                  User · Role · Institution · Email · Status · Joined On — six
                  columns of which a `Player` row can fill none: a child has no
                  email, no login and no join date, and "Role" is Student for
                  every row in the table. A column that can never fill is a
                  promise the data cannot keep, so they are gone rather than
                  dashed — `TeachersScreen` records the same removal.
                */}
                {paged.length === 0 ? (
                  <Tr>
                    <Td soft>
                      <span className="block p-6 text-center">
                        {rows.length === 0
                          ? 'No children on the platform yet.'
                          : 'No child matches these filters.'}
                      </span>
                    </Td>
                  </Tr>
                ) : (
                  paged.map((row, i) => {
                    const state = activityState(row, now)
                    return (
                      <Tr key={row.playerId}>
                        {/* Continuing across pages, so row 11 is 11 and not 1. */}
                        <Td soft>{(page - 1) * pageSize + i + 1}</Td>
                        <Td bold>{row.name}</Td>
                        <Td soft>{row.rollNumber ?? '—'}</Td>
                        <Td soft>{row.className ?? '—'}</Td>
                        <Td>
                          {/* 🔴 A CHILD WITH NO CLASS HAS NO SCHOOL — F1's
                              `unattributedChildren`, and the one row on this
                              screen that is a finding rather than a fact. */}
                          {row.schoolName ?? (
                            <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
                              No school
                            </StatusPill>
                          )}
                        </Td>
                        <Td soft>{BAND_LABEL[row.ageBand] ?? row.ageBand}</Td>
                        <Td num>{row.sessions}</Td>
                        <Td>
                          <StatusPill tone={ACTIVITY_TONE[state]} icon={ACTIVITY_ICON[state]}>
                            {ACTIVITY_LABEL[state]}
                          </StatusPill>
                        </Td>
                        <Td>
                          <button
                            type="button"
                            onClick={() => {
                              setOpenId(row.playerId)
                            }}
                            className="rounded-lg border border-line bg-white px-3 py-1 text-dash-xs font-bold text-ink transition-colors hover:bg-canvas cursor-pointer"
                          >
                            View
                          </button>
                        </Td>
                      </Tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </TableCard>

          {/*
            🔴 THE PAGE COUNT COMES FROM THE ROWS. `Pagination` renders nothing
            at all when there is one page, which is the honest answer for a
            platform with thirty-three children at fifty a page — and the reason
            a hardcoded "1877" could never have been right.
          */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={shown.length}
            pageSize={pageSize}
          />
        </div>
      </div>
    </section>
  )
}



/**
 * One child — when they played, for how long, and how much.
 *
 * 🔴 ITS OWN REQUEST, AND ITS OWN AUDIT ROW NAMING THIS CHILD. The list writes
 * one row about a roster; this writes one about a person, because they are
 * different acts and an incident asks about the second.
 */
function ChildDetailScreen({
  playerId,
  lang,
  auth,
  onBack,
}: {
  playerId: string
  lang: Lang
  auth?: StaffAuth
  onBack: () => void
}) {
  const t = PLATFORM_COPY[lang]

  const resource = useResource<PlatformChildDetail>(
    (signal) => getPlatformChild(playerId, { ...auth, signal }),
    ['platform-child', playerId],
  )

  return (
    <section className="font-dash text-dash-sm text-ink">
      <div className="mb-4 flex items-center gap-3">
        <BackButton onClick={onBack} label={t.childBack} />
        <span className="text-dash-xs font-bold text-muted">{t.childBack}</span>
      </div>

      <DataSurface
        state={resource.state}
        skeletonRows={6}
        errorMessage={describePlatformError(resource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={resource.retry}
      >
        {resource.data === null ? null : (
          <ChildDetailBody detail={resource.data} lang={lang} />
        )}
      </DataSurface>
    </section>
  )
}

function ChildDetailBody({ detail, lang }: { detail: PlatformChildDetail; lang: Lang }) {
  const t = PLATFORM_COPY[lang]
  const { child, sessions } = detail
  const accuracy = accuracyPair(child)
  const minutes = minutesPlayed(sessions)

  return (
    <>
      <PageHead
        title={child.name}
        subtitle={[
          child.schoolName,
          child.className,
          child.rollNumber === null ? null : `${t.childRoll} ${child.rollNumber}`,
        ]
          .filter((part): part is string => part !== null)
          .join(' · ')}
      />

      {child.schoolId === null && (
        <div className="mt-4 max-w-prose">
          <Note tone="warning" icon={<AlertIcon size={16} />} detail={t.childUnattributedHint}>
            {t.childUnattributed}
          </Note>
        </div>
      )}

      <SectionHead>{t.overviewTotals}</SectionHead>
      <StatGrid>
        <StatCard
          label={t.childSessions}
          value={child.sessions}
          icon={<PlayIcon size={18} />}
          tone="primary"
          source={{
            what: 'Every play session ever started for this child, at school or at home. Sessions with recording off and sessions that never closed are counted too.',
            from: 'Play sessions',
          }}
        />
        <StatCard
          label={t.childRounds}
          value={child.rounds}
          icon={<PlayIcon size={18} />}
          tone="teal"
          source={{
            what: 'Rounds this child played through to the end, in any game, since they first played. A round left part-way is not counted, a replayed level counts again, and sittings with recording off add nothing.',
            from: 'Game results saved at the end of each round',
          }}
        />
        <StatCard
          label={t.childGames}
          value={child.gamesPlayed}
          icon={<ChildrenIcon size={18} />}
          tone="leaf"
          source={{
            what: 'How many different Kalqy games this child has done anything in — answered, moved or finished a round — since they first played. Each game counts once, however often it was played.',
            from: 'Game results saved from each play session',
          }}
        />
        <StatCard
          label={t.childLastPlayed}
          value={
            child.lastPlayedAt === null
              ? t.childNever
              : new Date(child.lastPlayedAt).toLocaleDateString()
          }
          caption={
            child.firstPlayedAt === null
              ? undefined
              : `${t.since} ${new Date(child.firstPlayedAt).toLocaleDateString()}`
          }
          icon={<SchoolIcon size={18} />}
          tone="indigo"
          source={{
            what: 'The date this child’s games last saved anything — an answer, a movement or a finished round — and "Since" is the first such date. A sitting with recording off saves nothing, so it moves neither date.',
            from: 'Game results saved from each play session',
          }}
        />
      </StatGrid>

      {/*
        🔴 THE ANSWER PAIR, AND ONLY ABOVE THE FLOOR. Below ten answers there is
        no accuracy worth reading — "one attempt is 0 % or 100 %, which is every
        child on their first day" (`attention.ts`) — so the screen says how few
        rather than rendering a number an operator would sort by.
      */}
      <div className="mt-4 max-w-prose">
        <Note tone="neutral" icon={<InfoIcon size={16} />} detail={minutesLine()}>
          {accuracy === null
            ? t.childTooFewAnswers(MIN_ANSWERS_FOR_ACCURACY)
            : t.childPlayedPair(accuracy.correct, accuracy.total)}
        </Note>
      </div>

      {child.sessions === 0 ? (
        <div className="mt-8 max-w-prose">
          <Note tone="neutral" icon={<InfoIcon size={16} />}>
            {t.childNothingPlayed}
          </Note>
        </div>
      ) : (
        <>
          <div className="mt-8">
            <GamesPanel
              games={detail.games}
              summary={detail.summary}
              lang={lang}
              copy={{ ...GAMES_PANEL_COPY[lang], title: t.childGamesTitle }}
            />
          </div>

          <SectionHead
            count={sessions.length}
            hint={t.childSessionsHint(sessions.length, detail.sessionsTotal)}
          >
            {t.childSessionsTitle}
          </SectionHead>

          <TableCard>
            <table className="w-full border-collapse">
              <Thead
                headers={[
                  t.childSessionStarted,
                  t.childSessionLength,
                  t.childRounds,
                  t.childSessionEvents,
                  t.childClass,
                  t.childSessionVersion,
                ]}
              />
              <tbody>
                {sessions.map((session) => (
                  <Tr key={session.id}>
                    <Td>
                      {new Date(session.startedAt).toLocaleString()}
                      {!session.recorded && (
                        <span className="mt-1 block">
                          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
                            {t.childSessionUnrecorded}
                          </StatusPill>
                        </span>
                      )}
                    </Td>
                    {/* 🔴 "Still open", NEVER "0 minutes". The device closes a
                        session; a laptop that lost power never does, and a zero
                        would say the child sat down and left. */}
                    <Td num soft>
                      {session.minutes === null
                        ? t.childSessionOpen
                        : `${String(session.minutes)} min`}
                    </Td>
                    <Td num>{session.rounds}</Td>
                    <Td num>{session.events}</Td>
                    <Td soft>{session.className ?? t.dash}</Td>
                    <Td soft>{session.contentVersion ?? t.dash}</Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </TableCard>

          {sessions.some((s) => !s.recorded) && (
            <p className="mt-3 max-w-prose text-dash-xs text-muted">
              {t.childSessionUnrecordedHint}
            </p>
          )}
        </>
      )}
    </>
  )

  function minutesLine(): string {
    if (minutes === null) return t.childMinutesUnknown
    return t.childMinutes(minutes.minutes, minutes.measured, minutes.ofSessions)
  }
}
